import {getDefinitionForEvent, getDefinitionForProperty} from '../../util/definitions';
import moment from 'moment';
import {abbreviateNumber} from '../../util/index';
import {getCustomEventComposition} from '../custom-event-composition/util';
import {isPersistedCustomProperty} from '../custom-property-modal/util';

/**
 * @typedef {import("../../util/definitions-types").MpEvent} MpEvent
 * @typedef {import("../../util/definitions-types").MpProperty} MpProperty
 * @typedef {import("../../util/definitions-types").Cohort} Cohort
 */

const TYPE_ICONS = {
  string: `type-text`,
  number: `type-number`,
  boolean: `type-boolean`,
  datetime: `type-date`,
  list: `type-list`,
};

function getHeaders({name, displayName}) {
  if (displayName) {
    return {header: displayName, subheader: `Tracked as: ${name}`};
  } else {
    return {header: name, subheader: null};
  }
}

/**
 * @param {MpEvent} mpEvent
 * @param {EventDefinition[]} eventDefinitions
 * @param {Set<string>} permissions
 * @return {TooltipData}
 */
export function getTooltipDataForEvent(mpEvent, eventDefinitions, permissions, allowEdit = true) {
  const def = getDefinitionForEvent(mpEvent, eventDefinitions);

  let tooltipData = null;

  if (def) {
    const details = [];
    const metrics = [];

    if (def.isMixpanelDefinition) {
      details.push({icon: `info`, name: `Automatic`, value: `Automatically tracked by Mixpanel`});
    }

    if (def.platforms && def.platforms.length) {
      details.push({icon: `dataset`, name: `Platforms`, value: def.platforms.join(`, `)});
    }

    if (def.tags && def.tags.length) {
      details.push({icon: `tag`, name: `Tags`, value: def.tags.join(`, `)});
    }

    if (def.count) {
      metrics.push({name: `volume (30 days)`, value: abbreviateNumber(def.count, 1, true)});
    }

    if (def.uiQueryCount || def.apiQueryCount) {
      const queryCount = +def.uiQueryCount + +def.apiQueryCount;
      metrics.push({name: `queries (30 days)`, value: abbreviateNumber(queryCount, 1, true)});
    }

    let compositionDef = null;
    if (def.customEventId) {
      const defToCustomEvent = (def) => ({
        id: def.customEventId || def.collectEverythingEventId,
        name: def.name,
        alternatives: def.alternatives,
      });
      const customEvent = defToCustomEvent(def);
      const customEventMap = eventDefinitions
        .filter((def) => def.customEventId || def.collectEverythingEventId)
        .map((def) => defToCustomEvent(def))
        .reduce((map, customEvent) => Object.assign(map, {[`$custom_event:${customEvent.id}`]: customEvent}), {});
      compositionDef = getCustomEventComposition({customEvent}, !!customEvent.collectEverythingEventId, customEventMap);
    }

    let actions = null;
    if (allowEdit && compositionDef && permissions && permissions.has(`write_customevents`)) {
      actions = [
        {
          name: `editCustomEvent`,
          label: `Edit Custom Event`,
          item: {
            id: def.customEventId || def.collectEverythingEventId,
          },
        },
      ];
    }

    if (def.collectEverythingEventId) {
      details.push({icon: `autotrack`, name: `AutoTrack Event`, value: `Codeless tracking`});
    }

    tooltipData = {
      ...getHeaders(def),
      description: def.description,
      details,
      metrics,
      compositionDef,
      actions,
    };
  }

  return tooltipData;
}

/**
 * @param {MpProperty} mpProperty
 * @param {PropertyDefinition[]} propertyDefinitions
 * @return {TooltipData}
 */
export function getTooltipDataForProperty(mpProperty, propertyDefinitions, allowEdit = true) {
  const def = getDefinitionForProperty(mpProperty, propertyDefinitions);

  let tooltipData = null;

  if (def && !def.sensitive) {
    const metrics = [];
    const details = [];

    if (def.type) {
      details.push({icon: TYPE_ICONS[def.type] || TYPE_ICONS.string, name: `Type`, value: def.type});
    }

    if (def.exampleValue) {
      details.push({icon: `info`, name: `Example`, value: def.exampleValue});
    }

    if (def.count) {
      const name = def.resourceType === `user` ? `users with property` : `events with property`;
      metrics.push({name, value: abbreviateNumber(def.count, 1, true)});
    }

    if (def.uiQueryCount || def.apiQueryCount) {
      const name = `queries (30 days)`;
      const count = +def.uiQueryCount + +def.apiQueryCount;
      metrics.push({name, value: abbreviateNumber(count, 1, true)});
    }

    let actions = null;
    if (allowEdit && def.customPropertyId) {
      actions = [
        {
          name: `editCustomProperty`,
          label: isPersistedCustomProperty(def.customPropertyId) ? `Edit Custom Property` : `Edit Formula`,
          item: {
            property: {
              customPropertyId: def.customPropertyId,
              customProperty: def.customProperty,
            },
          },
          ...(mpProperty.actionDisabled ? {disabled: true} : {}),
        },
      ];
    }

    tooltipData = {
      ...getHeaders(def),
      description: def.description,
      details,
      metrics,
      actions,
    };
  }

  return tooltipData;
}

/**
 * @param {Cohort} cohort
 * @return {TooltipData}
 */
export function getTooltipDataForCohort(cohort) {
  const isInlineCohort = !!(cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort);
  if (!cohort.name && !isInlineCohort) {
    return null;
  }

  const details = [];
  const metrics = [];
  let subheader = null;

  if (cohort.createdBy) {
    subheader = `Created by ` + cohort.createdBy.name;
  } else if (isInlineCohort) {
    subheader = `Unsaved cohort`;
  }

  if (cohort.lastEdited) {
    subheader += ` - ` + moment(new Date(cohort.lastEdited)).format(`ll`);
  }

  if (Number.isInteger(cohort.count)) {
    const descriptor = cohort.dataGroupId ? `uniques` : `users`;
    metrics.push({name: `${descriptor} in cohort`, value: cohort.count.toLocaleString()});
  }

  let actions = null;
  if (cohort.value) {
    actions = [
      {
        name: `editCohort`,
        label: `Edit Cohort`,
        icon: `share`,
        item: isInlineCohort
          ? cohort
          : {
              id: cohort.value,
              dataGroupId: cohort.dataGroupId,
            },
      },
    ];
  }

  return {
    header: isInlineCohort ? cohort.value.cohort.raw_cohort.name : cohort.name,
    subheader,
    isUnsaved: isInlineCohort,
    description: cohort.description,
    details,
    metrics,
    actions,
  };
}
